/* The header control and the panel it opens, as one component.
 *
 * **One component rather than a button in the shell and a panel beside it**, because the open state
 * and the keyboard binding belong together: a shell holding the boolean would have to pass it to
 * both, and the `?` listener would then live a layer above the only thing that reads it.
 *
 * The button renders the reference's help-circle with a `?` chip beside the word, and its `title`
 * names the shortcut — the one place a reader who never presses `?` finds out it exists.
 */

import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { GuidePanel } from './GuidePanel';
import { useGuide } from './useGuide';

export function GuideLauncher(): ReactElement {
  const { t } = useTranslation();
  const guide = useGuide();

  return (
    <>
      <button
        type="button"
        className="lq-guide-button"
        onClick={guide.toggle}
        aria-expanded={guide.open}
        aria-label={t('guide.open')}
        title={t('guide.openHint')}
      >
        <svg
          className="lq-guide-button__icon"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <path d="M12 17h.01" />
        </svg>
        {t('guide.label')}
        <span className="lq-guide-button__kbd" aria-hidden="true">
          ?
        </span>
      </button>
      {guide.open ? <GuidePanel onClose={guide.close} /> : null}
    </>
  );
}
